import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiHeart, FiTrendingUp, FiBookOpen, FiEdit3 } from "react-icons/fi";

const BENEFITS = [
  { icon: FiHeart, img: "/web_images/Andlegur%20web%2030.webp", title: "Reduces Stress and Anxiety", text: "Writing about worries reduces their emotional weight. What lives in your head as a monster shrinks the moment you put it on the page. Research backs this. Your nervous system already knows it." },
  { icon: FiTrendingUp, img: "/web_images/Andlegur%20web%2031.webp", title: "Builds Deep Self-Awareness", text: "The patterns driving your life are invisible until you name them. Regular journaling makes them visible — the loop you keep running, the fear beneath the habit, the belief that is quietly steering everything." },
  { icon: FiBookOpen, img: "/web_images/Andlegur%20web%2032.webp", title: "Tracks Personal Growth", text: "Looking back at entries from three months ago shows you exactly how far you have come. Your journal becomes evidence that you are not stuck — you are moving, even when it does not feel like it." },
  { icon: FiEdit3, img: "/web_images/Andlegur%20web%2033.webp", title: "Processes Difficult Emotions", text: "Naming an emotion is one of the most effective ways to reduce its intensity. Writing gives form to the feeling — and once something has form, you can work with it instead of just surviving it." },
];

const MODES = [
  { title: "Guided Reflection Journaling", hook: "You will never face a blank page again.", body: "Andlegur delivers a personalized daily reflection prompt calibrated to your growth archetype. Not a generic question — the right question, right now. The one that finds the thing you have been circling without landing on.", img: "/web_images/Andlegur%20web%2025.webp", imgAlt: "Guided reflection journaling on Andlegur", reverse: false },
  { title: "Free Writing", hook: "Sometimes the most important journaling has no structure at all.", body: "Your private journal supports completely free writing — no prompts, no format, just you and your thoughts in a secure encrypted space. Every entry belongs to you alone. This is the space where the real stuff gets written.", img: "/web_images/Andlegur%20web%2026.webp", imgAlt: "Free writing journal on Andlegur", reverse: true },
  { title: "Emotional Check-In", hook: "You cannot change a pattern you cannot see.", body: "Track your emotional state daily. Andlegur visualizes your emotional patterns across weeks and months — so you start to see the triggers, the cycles, the conditions under which you thrive or contract. Then you can actually do something about them.", img: "/web_images/Andlegur%20web%2028.webp", imgAlt: "Emotional check-in journaling on Andlegur", reverse: false },
];

const JournalingPage = () => (
  <>
    <Helmet>
      <title>Online Journaling for Personal Growth | Daily Guided Journal | Andlegur</title>
      <meta name="description" content="Discover how guided daily journaling transforms self-awareness, emotional clarity, and personal growth. Start your free online journal with Andlegur — available on iOS, Android, and web." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://andlegur.com/journaling" />
    </Helmet>

    <div className="bg-[#05091B] text-indigo-100 overflow-x-hidden">

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%202.webp" alt="" className="w-full h-full object-cover opacity-30 mix-blend-screen" loading="eager" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/50 to-[#05091B]/60" />
          <div className="absolute inset-0 bg-linear-to-r from-[#05091B] via-transparent to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-32">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-indigo-400 text-xs uppercase tracking-widest mb-5">
            The Practice That Changes Everything
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            You have had thoughts you have never said out loud.<br />
            <span className="text-indigo-300">Some of them are the most important ones.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="text-indigo-200 text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            Journaling is not about writing. It is about finally hearing yourself. Andlegur gives you the structure, the prompts, and the space to do that — every single day.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join" className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl">Start Journaling Free →</Link>
            <Link to="/download" className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300">Get the App</Link>
          </motion.div>
        </div>
      </section>

      {/* Problem section */}
      <section className="py-24 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 gap-14 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: "460px" }}>
              <img src="/web_images/Andlegur%20web%2018.webp" alt="The cost of unexpressed thoughts — why journaling matters" className="w-full h-full object-cover absolute inset-0" loading="lazy" />
              <div className="absolute inset-0 bg-linear-to-t from-indigo-950/80 via-transparent to-transparent" />
              <p className="absolute bottom-8 left-8 right-8 text-white text-xl font-light italic leading-snug">
                "After 7 days of guided journaling, most Andlegur members describe their relationship with themselves as completely different."
              </p>
            </div>
            <div>
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">Why It Matters</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-5 leading-snug">
                Everything you are carrying in your head<br />
                <span className="text-indigo-300">is costing you clarity, sleep, and peace.</span>
              </h2>
              <div className="w-12 h-px bg-indigo-500 mb-7" />
              <p className="text-indigo-200 text-base leading-relaxed mb-5">
                Research in psychology and neuroscience consistently shows that expressive writing — putting thoughts and feelings into words — helps the brain process difficult experiences, reduce emotional reactivity, and build resilience.
              </p>
              <p className="text-indigo-300 text-base leading-relaxed">
                Journaling works because it externalizes your internal world. It gives you distance and perspective that is impossible when thoughts stay inside your head — circling, compounding, never resolving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefit cards */}
      <section className="py-20 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">What Changes</p>
            <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">
              Four things that shift<br /><span className="text-indigo-300">the moment you start writing honestly.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map(({ icon: Icon, img, title, text }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="bg-[#0E1833] border border-indigo-700/40 rounded-2xl overflow-hidden flex flex-col group">
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <div className="w-9 h-9 rounded-xl border border-indigo-400/40 bg-white/5 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-indigo-300" />
                  </div>
                  <h3 className="text-white font-semibold">{title}</h3>
                  <p className="text-indigo-300 text-sm leading-relaxed">{text}</p>
                </div>
                <div className="relative h-40 overflow-hidden shrink-0">
                  <img src={img} alt={title} className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-linear-to-t from-[#0E1833]/60 via-transparent to-transparent" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Journaling modes */}
      <section className="py-20 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6 space-y-20">
          <div className="text-center">
            <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">Three Ways to Journal</p>
            <h2 className="text-3xl sm:text-4xl font-light text-white">
              Different moods.<br /><span className="text-indigo-300">Different depths. All valuable.</span>
            </h2>
          </div>
          {MODES.map(({ title, hook, body, img, imgAlt, reverse }) => (
            <div key={title} className={`grid sm:grid-cols-2 gap-12 items-center ${reverse ? "sm:grid-flow-dense" : ""}`}>
              <div className={reverse ? "sm:col-start-2" : ""}>
                <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">{title}</p>
                <h3 className="text-2xl sm:text-3xl font-light text-white mb-4 leading-snug">
                  <em className="text-indigo-300 not-italic">{hook}</em>
                </h3>
                <div className="w-10 h-px bg-indigo-500 mb-5" />
                <p className="text-indigo-200 text-base leading-relaxed">{body}</p>
              </div>
              <div className={`relative rounded-2xl overflow-hidden shadow-2xl ${reverse ? "sm:col-start-1 sm:row-start-1" : ""}`} style={{ minHeight: "360px" }}>
                <img src={img} alt={imgAlt} className="w-full h-full object-cover absolute inset-0" loading="lazy" />
                <div className="absolute inset-0 bg-linear-to-t from-indigo-950/60 via-transparent to-transparent" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How to start */}
      <section className="relative py-28 overflow-hidden border-t border-indigo-800/40">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%2034.webp" alt="" className="w-full h-full object-cover opacity-15 mix-blend-screen" loading="lazy" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/70 to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4 text-center">Getting Started</p>
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-10 text-center leading-snug">
            The biggest barrier to journaling<br /><span className="text-indigo-300">is not motivation. It is structure.</span>
          </h2>
          <ol className="space-y-6">
            {[
              ["Choose your time", "Morning reflection sets intention; evening journaling processes and releases. Both work. Start with one."],
              ["Start with five minutes", "Consistency matters more than length. Five focused minutes every day outperforms an hour once a week — always."],
              ["Use guided prompts", "Andlegur solves the blank-page problem entirely. Your prompt is waiting every morning, calibrated to your archetype."],
              ["Write what is true", "Journaling only transforms when you write what is actually happening — not what sounds good. Your journal is private and encrypted."],
              ["Look back", "Re-reading past entries is where much of the insight lives. You will see patterns you could not see in the moment."],
            ].map(([step, detail], i) => (
              <li key={step} className="flex gap-5 items-start">
                <span className="shrink-0 w-9 h-9 rounded-full bg-indigo-700 text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <span className="text-white font-semibold">{step}</span>
                  <span className="text-indigo-300"> — {detail}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden border-t border-indigo-800/40">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%2035.webp" alt="" className="w-full h-full object-cover opacity-20 mix-blend-screen" loading="lazy" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/60 to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-light text-white mb-5 leading-tight">
            Your most important thoughts<br /><span className="text-indigo-300">deserve to finally be heard.</span>
          </h2>
          <p className="text-indigo-300 text-lg leading-relaxed mb-10">
            Start your free guided journaling practice today. Discover your archetype, receive your first personalized prompt, and begin the habit that changes how you understand everything — including yourself. Free on iOS, Android, and web.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join" className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl">Start Journaling Free →</Link>
            <Link to="/download" className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300">Download the App</Link>
          </div>
        </div>
      </section>
    </div>
  </>
);

export default JournalingPage;
